import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarBlank, MapPin, Clock, Users, CheckCircle, UserPlus } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  treino: "from-blue-500 to-cyan-500",
  jogo: "from-green-500 to-emerald-500",
  torneio: "from-orange-500 to-red-500",
  evento_social: "from-purple-500 to-pink-500",
  evento: "from-teal-500 to-cyan-500",
};

const categoryLabels = {
  treino: "Treino",
  jogo: "Jogo",
  torneio: "Torneio",
  evento_social: "Evento Social",
  evento: "Evento",
};

const Calendar = () => {
  const { user, getAuthHeaders } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/events`, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      setEvents(res.data);
    } catch (err) {
      console.error("Erro ao carregar eventos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRsvp = async (eventId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/events/${eventId}/rsvp`, {}, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      toast.success(res.data.message);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao confirmar presença");
    }
  };

  const categories = ["all", ...new Set(events.map((e) => e.category))];

  const filtered = filter === "all" ? events : events.filter((e) => e.category === filter);

  // Group events by date
  const grouped = filtered.reduce((acc, ev) => {
    const d = ev.date;
    if (!acc[d]) acc[d] = [];
    acc[d].push(ev);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });
  };

  if (loading) return <div className="text-center py-12 text-[#115E59]">Carregando eventos...</div>;

  return (
    <div data-testid="calendar-tab">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-2">Calendário</h2>
      <p className="text-[#115E59] mb-6">Treinos, jogos, torneios e eventos do clube</p>

      {/* Category filter */}
      <div className="flex overflow-x-auto gap-2 mb-8 pb-2 no-scrollbar">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={`px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all border-2 ${
              filter === cat
                ? "bg-[#042F2E] text-white border-[#042F2E]"
                : "bg-white text-[#115E59] border-[#E4DFD3] hover:border-[#FF5A26]"
            }`}
            data-testid={`filter-${cat}`}
          >
            {cat === "all" ? "Todos" : categoryLabels[cat] || cat}
          </button>
        ))}
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E4DFD3]">
          <CalendarBlank size={64} weight="duotone" className="text-[#E4DFD3] mx-auto mb-4" />
          <p className="text-xl text-[#115E59]">Nenhum evento agendado</p>
          <p className="text-sm text-[#115E59] mt-2">Fique de olho, novos eventos serão publicados em breve!</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([date, evts]) => (
            <div key={date}>
              <h3 className="text-sm font-bold text-[#115E59] uppercase tracking-wider mb-4 flex items-center gap-2">
                <CalendarBlank size={18} weight="bold" />
                {formatDate(date)}
              </h3>
              <div className="space-y-4">
                {evts.map((ev, i) => {
                  const isAttending = ev.attendees?.includes(user?.id);
                  const color = categoryColors[ev.category] || categoryColors.evento;
                  return (
                    <motion.div
                      key={ev.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="bg-white rounded-2xl border border-[#E4DFD3] overflow-hidden shadow-sm"
                      data-testid={`event-${ev.id}`}
                    >
                      <div className={`h-1.5 bg-gradient-to-r ${color}`} />
                      <div className="p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <span className={`inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3 bg-gradient-to-r ${color} text-white`}>
                              {categoryLabels[ev.category] || ev.category}
                            </span>
                            <h4 className="text-lg font-bold text-[#042F2E] mb-2">{ev.title}</h4>
                            {ev.description && <p className="text-sm text-[#115E59] mb-3">{ev.description}</p>}
                            <div className="flex flex-wrap gap-4 text-sm text-[#115E59]">
                              <span className="flex items-center gap-1.5">
                                <Clock size={16} weight="duotone" />
                                {ev.time_start}{ev.time_end ? ` - ${ev.time_end}` : ""}
                              </span>
                              <span className="flex items-center gap-1.5">
                                <MapPin size={16} weight="duotone" />
                                {ev.location}
                              </span>
                              <span className="flex items-center gap-1.5">
                                <Users size={16} weight="duotone" />
                                {ev.attendees?.length || 0}{ev.max_attendees > 0 ? `/${ev.max_attendees}` : ""} confirmados
                              </span>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleRsvp(ev.id)}
                            className={`shrink-0 rounded-full font-bold px-5 ${
                              isAttending
                                ? "bg-[#10B981] hover:bg-[#059669] text-white"
                                : "bg-[#FF5A26] hover:bg-[#E04818] text-white"
                            }`}
                            data-testid={`rsvp-${ev.id}`}
                          >
                            {isAttending ? (
                              <><CheckCircle size={18} weight="fill" className="mr-2" /> Confirmado</>
                            ) : (
                              <><UserPlus size={18} weight="bold" className="mr-2" /> Confirmar</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Calendar;
